import { Plugin, PluginKey } from 'prosemirror-state';
import { findChildrenByType } from 'prosemirror-utils';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { codeBlockLangAttr, codeBlockNodeName, codeBlockType } from '../../../extensions/markdown';
import { capitalize } from '../../../lodash';
import { logger } from '../../../logger';
import { codeLangSelectTooltipViewCreator } from '../CodeMirrorView/TooltipPlugin';
const key = new PluginKey('code_block_highlight');
export const CodeBlockHighlight = (builder, opts) => {
    let langs;
    let lowlight;
    let hljs;
    try {
        hljs = require('highlight.js/lib/core');
        const low = require('lowlight');
        const all = low.all;
        const create = low.createLowlight;
        langs = Object.assign(Object.assign({}, all), opts.langs);
        lowlight = create(langs);
    }
    catch (e) {
        logger.info('Skip code_block highlighting');
        return;
    }
    builder.addPlugin(() => {
        const selectItems = [];
        const mapping = {};
        for (const lang of Object.keys(langs)) {
            const defs = langs[lang](hljs);
            selectItems.push({
                value: lang,
                content: defs.name || capitalize(lang),
            });
            if (defs.aliases) {
                for (const alias of defs.aliases) {
                    mapping[alias] = lang;
                }
            }
        }
        // TODO: add TAB key handler
        // TODO: Remove constant selection of block
        return new Plugin({
            key,
            state: {
                init: (_, state) => getDecorations(state.doc),
                apply: (tr, decos, oldState, newState) => {
                    if (tr.docChanged) {
                        const oldNodeName = oldState.selection.$head.parent.type.name;
                        const newNodeName = newState.selection.$head.parent.type.name;
                        // Apply decorations if:
                        if (oldNodeName === codeBlockNodeName ||
                            newNodeName === codeBlockNodeName) {
                            // selection includes codeblock node,
                            return getDecorations(tr.doc);
                        }
                        else {
                            const oldNodes = findChildrenByType(oldState.doc, codeBlockType(oldState.schema));
                            const newNodes = findChildrenByType(newState.doc, codeBlockType(newState.schema));
                            if (
                            // OR transaction adds/removes codeblock nodes,
                            newNodes.length !== oldNodes.length ||
                                // OR transaction has changes that completely encapsulate a node
                                // (for example, a transaction that affects the entire document).
                                // Such transactions can happen during collab syncing via y-prosemirror, for example.
                                tr.steps.some((step) => {
                                    return (stepHasFromTo(step) &&
                                        oldNodes.some((node) => node.pos >= step.from &&
                                            node.pos + node.node.nodeSize <= step.to));
                                })) {
                                return getDecorations(tr.doc);
                            }
                        }
                    }
                    return decos.map(tr.mapping, tr.doc);
                },
            },
            view: (view) => codeLangSelectTooltipViewCreator(view, selectItems, mapping),
            props: {
                decorations: (state) => {
                    return key.getState(state);
                },
                nodeViews: {
                    [codeBlockNodeName]: (node) => {
                        let prevLang = node.attrs[codeBlockLangAttr];
                        const dom = document.createElement('pre');
                        const contentDOM = document.createElement('code');
                        contentDOM.classList.add('hljs');
                        if (prevLang) {
                            dom.setAttribute(codeBlockLangAttr, prevLang);
                            contentDOM.classList.add(prevLang);
                        }
                        dom.append(contentDOM);
                        return {
                            dom,
                            contentDOM,
                            update(newNode) {
                                if (node.type !== newNode.type)
                                    return false;
                                const newLang = newNode.attrs[codeBlockLangAttr];
                                if (prevLang !== newLang) {
                                    contentDOM.className = 'hljs';
                                    if (newLang) {
                                        dom.setAttribute(codeBlockLangAttr, newLang);
                                        contentDOM.classList.add(newLang);
                                    }
                                    else {
                                        dom.removeAttribute(codeBlockLangAttr);
                                    }
                                    prevLang = newLang;
                                }
                                return true;
                            },
                        };
                    },
                },
            },
        });
    });
    function getDecorations(doc) {
        const decos = [];
        for (const { node, pos } of findChildrenByType(doc, codeBlockType(doc.type.schema), true)) {
            let from = pos + 1;
            let nodes;
            const lang = node.attrs[codeBlockLangAttr];
            if (!lang) {
                nodes = lowlight.highlightAuto(node.textContent).children;
            }
            else if (lowlight.registered(lang)) {
                nodes = lowlight.highlight(lang, node.textContent).children;
            }
            else {
                continue;
            }
            for (const { text, classes } of parseNodes(nodes)) {
                const to = from + text.length;
                if (classes.length) {
                    decos.push(Decoration.inline(from, to, {
                        class: classes.join(' '),
                    }));
                }
                from = to;
            }
        }
        return DecorationSet.create(doc, decos);
    }
};
function parseNodes(nodes, className = []) {
    return nodes
        .map((node) => {
        var _a;
        let classes = className;
        if (node.type === 'element') {
            classes = classes.concat((_a = node.properties.className) !== null && _a !== void 0 ? _a : []);
            return parseNodes(node.children, classes);
        }
        return {
            text: node.type === 'comment' || node.type === 'text' ? node.value : '',
            classes,
        };
    })
        .flat();
}
function stepHasFromTo(step) {
    // @ts-expect-error
    return typeof step.from === 'number' && typeof step.to === 'number';
}
